import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook to handle automatic logout after inactivity
 * Tracks user activity via DOM events and API calls
 * 
 * @param {Function} onLogout - Callback function to execute on logout
 * @param {number} inactivityTimeout - Time in milliseconds before auto-logout (default: 15 minutes)
 * @param {number} warningTimeout - Time in milliseconds before showing warning (default: 30 seconds before logout)
 * @returns {Object} - Object containing showWarning state, remainingTime, and resetTimer function
 */
const useAutoLogout = (onLogout, inactivityTimeout = 15 * 60 * 1000, warningTimeout = 30 * 1000) => {
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const debounceTimerRef = useRef(null);
  
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  // Reset the timer on user activity
  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Hide warning if it was showing
    setShowWarning(false);
    setRemainingTime(0);

    // Update last activity time
    lastActivityRef.current = Date.now();

    // Set warning timeout (show warning before logout)
    const warningTime = inactivityTimeout - warningTimeout;
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      
      // Start countdown
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      
      countdownIntervalRef.current = setInterval(() => {
        const timeLeft = inactivityTimeout - (Date.now() - lastActivityRef.current);
        if (timeLeft > 0) {
          setRemainingTime(Math.ceil(timeLeft / 1000)); // Convert to seconds
        } else {
          clearInterval(countdownIntervalRef.current);
          setRemainingTime(0);
        }
      }, 1000);
    }, warningTime);

    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      onLogout();
    }, inactivityTimeout);
  }, [onLogout, inactivityTimeout, warningTimeout]);

  // Debounced activity handler to prevent excessive resets
  const handleActivity = useCallback(() => {
    // Clear previous debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new debounce (reset after 1 second of no activity)
    debounceTimerRef.current = setTimeout(() => {
      resetTimer();
    }, 1000);
  }, [resetTimer]);

  // Handle user activity events
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
    
    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause timer when tab is hidden (optional - you can remove this if you want timer to continue)
        // For now, we'll continue tracking but you can add pause logic here
      } else {
        // Resume/reset when tab becomes visible
        resetTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize timer on mount
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [handleActivity, resetTimer]);

  return { showWarning, remainingTime, resetTimer };
};

export default useAutoLogout;

